import { useNavigate } from 'react-router-dom';
import WhatHappensNext from './WhatHappensNext';

interface Props {
  subscriptionPaid: boolean;
  workwearPurchased: boolean;
  documentsUploaded: boolean;
}

export default function OnboardingComplete({ subscriptionPaid, workwearPurchased, documentsUploaded }: Props) {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Success Message */}
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce">
          <i className="ri-checkbox-circle-line text-4xl text-white"></i>
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          Application Submitted Successfully! 🎉
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Thank you for joining Taskbudy. Your application is now under review.
        </p>

        {/* Status Checklist */}
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 mb-8 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-4 text-left">Application Checklist</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-left">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${subscriptionPaid ? 'bg-green-500' : 'bg-yellow-500'}`}>
                <i className={`${subscriptionPaid ? 'ri-check-line' : 'ri-time-line'} text-white text-sm`}></i>
              </div>
              <span className="text-gray-700">
                {subscriptionPaid ? 'Subscription Payment Completed' : 'Subscription Payment Pending'}
              </span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${workwearPurchased ? 'bg-green-500' : 'bg-yellow-500'}`}>
                <i className={`${workwearPurchased ? 'ri-check-line' : 'ri-time-line'} text-white text-sm`}></i>
              </div>
              <span className="text-gray-700">
                {workwearPurchased ? 'Workwear Kit Ordered' : 'Workwear Kit Pending'}
              </span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${documentsUploaded ? 'bg-green-500' : 'bg-yellow-500'}`}>
                <i className={`${documentsUploaded ? 'ri-check-line' : 'ri-time-line'} text-white text-sm`}></i>
              </div>
              <span className="text-gray-700">
                {documentsUploaded ? 'Documents Uploaded' : 'Documents Pending'}
              </span>
            </div>
          </div>
        </div>

        {/* Pending Items Warning */}
        {(!subscriptionPaid || !workwearPurchased) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <i className="ri-alert-line text-2xl text-yellow-600 mt-1"></i>
              <div className="text-left">
                <h4 className="font-bold text-gray-900 mb-2">Action Required</h4>
                <p className="text-sm text-gray-700 mb-3">
                  You have pending items that need to be completed before you can start accepting jobs:
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  {!subscriptionPaid && <li>• Complete your subscription payment</li>}
                  {!workwearPurchased && <li>• Purchase your mandatory workwear kit</li>}
                </ul>
                <p className="text-sm text-gray-700 mt-3">
                  You can complete these from your provider dashboard.
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => navigate('/provider-dashboard')}
          className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] text-white rounded-lg hover:from-[#0B3C5D]/90 hover:to-[#0B3C5D]/90 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          Go to Dashboard
        </button>
      </div>

      {/* What Happens Next Timeline */}
      <WhatHappensNext />

      {/* Support Section */}
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-3xl text-white"></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Need Help?</h3>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you every step of the way
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                const tawkWidget = document.querySelector('#tawk-widget-floating-button') as HTMLElement;
                if (tawkWidget) {
                  tawkWidget.click();
                } else if ((window as any).Tawk_API) {
                  (window as any).Tawk_API.toggle();
                }
              }}
              className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white border-2 border-[#0B3C5D] text-[#0B3C5D] rounded-lg hover:bg-[#0B3C5D] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold"
            >
              <i className="ri-chat-3-line text-xl"></i>
              <span>Live Chat Support</span>
            </button>
            <a
              href="mailto:support@taskbudy.co.uk"
              className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer font-semibold"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Email Support</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
